import { useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';

import { Box, Heading, Paragraph, Tag, Text, TextButton } from '@zillow/constellation-10';

import { ApplicationSampleModal } from '@zg-rentals/applications-components';
import { FeeType, ForRentByType } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useCanAccessCondensedFlow } from '../../../../hooks';
import { useGetListingDetails, useGetListingRecommendations } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';
import numberUtils from '../../../../utils/numberUtils';

import { RECS } from '../../../../constants/RecommendationsConstants';
import { LEASE_DURATION } from '../Review/constants';
import { SF_MODAL_COMPONENTS } from '../Review/constants';

import { FieldEditModal } from '../../../modals/FieldEditModal';
import RecommendationModal from '../../../modals/RecommendationModal';
import ContentSectionEditButton from '../Review/components/ContentSectionEditButton';
import FieldValueDisplay from '../Review/components/FieldValueDisplay';

import {
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';
import { reviewSectionStyles, reviewSectionTitleStyles, reviewTitleRowStyles } from '../Review/styles';

import { emitClickSampleApplicationLinkOnPageEvent, EVENT_IDS } from '../../../../analytics/events/modularListing';
import { useAuth } from '../../../AuthProvider';
import PageWizard from '../../../Wizard/PageWizard';

import type { MouseEvent } from 'react';
import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const FastPostReview = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const canAccessCondensedFlow = useCanAccessCondensedFlow();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const recommendationTypes = new Set(recommendationsData?.recommendations?.map((r) => r.type) ?? []);

  const isMultifamily = listingDetails?.isMultifamily ?? false;

  const canAccessSFCostsAndFees = accessUtils.canAccessSFCostsAndFees({
    isFeed: listingDetails?.isFeed ?? false,
    isMultifamily,
  });

  const { data: applicationFee = [] } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType(
    {
      feeType: FeeType.APPLICATION_FEE,
      listingAlias,
    },
    {
      meta: { tags: ['sf-applicationFee'] },
      enabled: canAccessSFCostsAndFees && listingAlias !== '',
    },
  );

  const [componentKey, setComponentKey] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [displaySampleModal, setDisplaySampleModal] = useState(false);
  const [displayRecsModal, setDisplayRecsModal] = useState<string>();

  const feeAmount = applicationFee?.length ? applicationFee?.[0]?.feeAmountMin : null;
  const hideAddress = listingDetails?.hideStreetAddress ?? false;
  const forRentBy = listingDetails?.forRentByType ?? '';
  const leaseDuration = listingDetails?.leaseDuration ?? '';
  const hidePhone = listingDetails?.hidePhone ?? false;
  const isApplicationsAccepted = listingDetails?.isApplicationsAccepted;
  const isApplicationsUpdateRestricted = listingDetails?.restrictions?.applications?.isUpdateRestricted ?? true;
  const isHidePhoneRestricted = listingDetails?.restrictions?.isHidePhoneRestricted ?? false;

  const floorplan = listingDetails?.floorPlans?.[0];
  const pDateAvailable = floorplan?.dateAvailable;
  const dateAvailable = pDateAvailable ? new Date(pDateAvailable) : null;
  const displayDateAvailable =
    dateAvailable && isValid(dateAvailable)
      ? format(new Date(dateAvailable.setDate(dateAvailable.getDate() + 1)), 'MM/dd/yyyy')
      : undefined;

  const displayLeaseDuration = leaseDuration ? LEASE_DURATION[leaseDuration] : undefined;

  const handleEditClick = (event: MouseEvent<HTMLButtonElement>) => {
    const key = (event.currentTarget as HTMLButtonElement).value;
    setComponentKey(key);
    setIsModalOpen(true);
  };

  const modalEntry = SF_MODAL_COMPONENTS[componentKey as keyof typeof SF_MODAL_COMPONENTS];

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
    >
      <div data-testid="FastPostReview">
        <Box css={wizardFormSectionContainerStyles.raw()}>
          <Heading level={2} textStyle={canAccessCondensedFlow ? 'heading-md' : 'heading-sm'} css={wizardHeadingStyles}>
            Confirm final details
          </Heading>

          <Paragraph css={wizardParagraphStyles}>
            To save you time, we added a few details about your home. You can edit, if needed.
          </Paragraph>

          <div data-testid="FastPostReviewContent">
            <Box css={reviewSectionStyles} data-testid="HidePropertyAddress">
              <Box css={reviewTitleRowStyles}>
                <Box css={reviewSectionTitleStyles}>
                  <Text textStyle="body-bold">Hide property address</Text>
                  {recommendationTypes.has(RECS.HIDE_STREET_ADDRESS) && (
                    <TextButton
                      aria-label={`Learn more about ${SF_MODAL_COMPONENTS.HideAddressFormField.title}`}
                      textStyle="body-sm"
                      onClick={() => {
                        setDisplayRecsModal(RECS.HIDE_STREET_ADDRESS);
                      }}
                    >
                      Learn more
                    </TextButton>
                  )}
                </Box>

                <ContentSectionEditButton
                  fieldLabel={SF_MODAL_COMPONENTS.HideAddressFormField.title}
                  value={SF_MODAL_COMPONENTS.HideAddressFormField.componentKey}
                  onEditClick={handleEditClick}
                />
              </Box>
              <FieldValueDisplay value={hideAddress ? 'Yes' : 'No'} showIconPlaceholder={false} />
            </Box>

            <Box css={reviewSectionStyles} data-testid="DateAvailable">
              <Box css={reviewTitleRowStyles}>
                <Box css={{ display: 'flex', alignItems: 'center' }}>
                  <Text textStyle="body-bold">Date available</Text>
                </Box>

                <ContentSectionEditButton
                  fieldLabel={SF_MODAL_COMPONENTS.DateAvailableFormField.title}
                  value={SF_MODAL_COMPONENTS.DateAvailableFormField.componentKey}
                  onEditClick={handleEditClick}
                />
              </Box>
              <FieldValueDisplay value={displayDateAvailable} showIconPlaceholder={false} />
            </Box>

            <Box css={reviewSectionStyles} data-testid="LeaseDuration">
              <Box css={reviewTitleRowStyles}>
                <Box css={{ display: 'flex', alignItems: 'center' }}>
                  <Text textStyle="body-bold">Lease duration</Text>
                </Box>

                <ContentSectionEditButton
                  fieldLabel={SF_MODAL_COMPONENTS.LeaseDurationFormField.title}
                  value={SF_MODAL_COMPONENTS.LeaseDurationFormField.componentKey}
                  onEditClick={handleEditClick}
                />
              </Box>
              <FieldValueDisplay value={displayLeaseDuration} showIconPlaceholder={false} />
            </Box>

            {forRentBy !== ForRentByType.AGENT && !isHidePhoneRestricted && (
              <Box css={reviewSectionStyles} data-testid="AllowContactByPhone">
                <Box css={reviewTitleRowStyles}>
                  <Box css={reviewSectionTitleStyles}>
                    <Text textStyle="body-bold">Allow renters to contact you by phone</Text>
                    {recommendationTypes.has(RECS.HIDE_PHONE) && (
                      <TextButton
                        aria-label={`Learn more about ${SF_MODAL_COMPONENTS.AllowContactByPhoneFormField.title}`}
                        textStyle="body-sm"
                        onClick={() => {
                          setDisplayRecsModal(RECS.HIDE_PHONE);
                        }}
                      >
                        Learn more
                      </TextButton>
                    )}
                  </Box>

                  <ContentSectionEditButton
                    fieldLabel={SF_MODAL_COMPONENTS.AllowContactByPhoneFormField.title}
                    value={SF_MODAL_COMPONENTS.AllowContactByPhoneFormField.componentKey}
                    onEditClick={handleEditClick}
                  />
                </Box>

                <FieldValueDisplay value={hidePhone ? 'Yes' : 'No'} showIconPlaceholder={false} />
              </Box>
            )}

            {isApplicationsUpdateRestricted === false && (
              <Box css={reviewSectionStyles} data-testid="ZillowApplications">
                <Box css={reviewTitleRowStyles}>
                  <Box css={reviewSectionTitleStyles}>
                    <Text textStyle="body-bold">Accept Zillow online applications</Text>
                    <Box css={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                      <Tag tone="purple" size="sm" css={{ marginRight: '200' }}>
                        Free
                      </Tag>
                      <TextButton
                        aria-label={`See sample ${SF_MODAL_COMPONENTS.AcceptZillowApplicationsFormField.title}`}
                        textStyle="body-sm"
                        onClick={() => {
                          setDisplaySampleModal(true);
                          if (listingDetails && user) {
                            emitClickSampleApplicationLinkOnPageEvent({
                              currentProperty: listingDetails,
                              user,
                            });
                          }
                        }}
                        data-eventid={EVENT_IDS.CLICK_SAMPLE_APPLICATION_LINK_ON_PAGE}
                      >
                        Sample
                      </TextButton>
                    </Box>
                  </Box>

                  <ContentSectionEditButton
                    fieldLabel={SF_MODAL_COMPONENTS.AcceptZillowApplicationsFormField.title}
                    value={SF_MODAL_COMPONENTS.AcceptZillowApplicationsFormField.componentKey}
                    onEditClick={handleEditClick}
                    allowFeedEdit={true}
                  />
                </Box>

                <FieldValueDisplay
                  value={isApplicationsAccepted === undefined ? undefined : isApplicationsAccepted ? 'Yes' : 'No'}
                  showIconPlaceholder={false}
                />
                {canAccessSFCostsAndFees && feeAmount !== null && !isApplicationsAccepted && (
                  <Box css={{ marginTop: '400' }}>
                    <FieldValueDisplay
                      value={`Application fee: ${numberUtils.asCurrency(feeAmount, feeAmount % 1 != 0 ? '$0,0.00' : '$0,0')}`}
                      showIconPlaceholder={false}
                    />
                  </Box>
                )}
              </Box>
            )}

            {displayRecsModal && (
              <RecommendationModal onClose={() => setDisplayRecsModal('')} recommendationType={displayRecsModal} />
            )}
            {displaySampleModal && <ApplicationSampleModal onClose={() => setDisplaySampleModal(false)} />}
          </div>
        </Box>

        {isModalOpen && modalEntry && (
          <FieldEditModal title={modalEntry.title} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <modalEntry.component />
          </FieldEditModal>
        )}
      </div>
    </PageWizard>
  );
};

export default FastPostReview;
